import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Input,
	MenuItem,
	Select,
	Stack,
	Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import axios, { AxiosError } from 'axios';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import {
	ArrayField,
	BooleanInput,
	Datagrid,
	DateInput,
	Edit,
	FormDataConsumer,
	FormTab,
	FunctionField,
	ImageField,
	ImageInput,
	NumberInput,
	ReferenceArrayInput,
	SaveButton,
	SelectArrayInput,
	SelectInput,
	TabbedForm,
	TextField,
	TextInput,
	Toolbar,
	useEditController,
	useGetRecordId,
	useNotify,
	useRedirect,
} from 'react-admin';
import { MdDelete } from 'react-icons/md';
import constants from '../../constants';
import { getLocalizedErrorMessage } from '../../utils/functions';
import PrivateServicesTable from './PrivateServicesTable';
import InstructorFullExportButton from './InstructorFullExportButton';
import ExpertVacations from './ExpertVacations';

// Open a document/image in a new tab. Chrome BLOCKS top-level navigation to data: URLs (shows a
// blank page), so for base64 docs (certificates are stored as data-URIs) we decode to a Blob and
// open an object-URL instead; regular http(s) URLs open directly. Never throws.
const openDocInNewTab = (e: any, url: string) => {
	if (e) e.preventDefault();
	try {
		if (url && url.startsWith('data:')) {
			const comma = url.indexOf(',');
			const mime = url.substring(5, comma).split(';')[0] || 'application/octet-stream';
			const bin = atob(url.substring(comma + 1));
			const bytes = new Uint8Array(bin.length);
			for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
			const obj = URL.createObjectURL(new Blob([bytes], { type: mime }));
			window.open(obj, '_blank');
			setTimeout(() => URL.revokeObjectURL(obj), 60000);
		} else {
			window.open(url, '_blank');
		}
	} catch (err) {
		window.open(url, '_blank');
	}
};

export const ExpertEdit = () => {
	const [newRecommendation, setNewRecommendation] = useState({ name: '', text: '', language: 'he' });
	const [privateServices, setPrivateServices] = useState<any[]>();
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [professionalCertificateFiles, setProfessionalCertificateFiles] = useState<File[]>([]);
	const [isUploadingCertificates, setIsUploadingCertificates] = useState(false);
	const [galleryImages, setGalleryImages] = useState<any[]>([]);
	const [aiTags, setAiTags] = useState<any[]>([]);
	const notify = useNotify();
	const current_instructor = useEditController();
	const instructor_id = useGetRecordId();
	const redirect = useRedirect();
	// GH-791 — archive dialog; the list this expert belongs to (also the target after deactivate/archive).
	const [archiveOpen, setArchiveOpen] = useState(false);
	const [archiving, setArchiving] = useState(false);
	const listPath = `/instructor/allin/${current_instructor?.record?.safe_space ? 'safe-space' : 'experts'}`;

	const getPrivateServices = useCallback(async () => {
		try {
			const response = await axios.get(`${constants.API_URL}/instructor/private_services/all`, {
				params: { instructor_id },
			});
			console.log(response.data);
			setPrivateServices(response.data);
		} catch (error) {}
	}, [setPrivateServices, instructor_id]);

	const fetchGalleryImages = useCallback(async () => {
		try {
			const response = await axios.get(`${constants.API_URL}instructor/get_suppliers_gallery`, {
				params: { instructor_id }
			});
			setGalleryImages(response.data);
		} catch (error) {
			console.error('Error fetching gallery:', error);
			notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
		}
	}, [instructor_id, notify]);

	const getAiTags = useCallback(async () => {
		try {
			const response = await axios.get(`${constants.API_URL}aitag`, {
				
			});
		if(response.data){
			if(response.data?.data && response.data.data.length > 0){
				setAiTags(response.data.data);
			}
			
		}
		
			
		} catch (error) {
			console.error('Error fetching ai tags:', error);
			notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
		}
	}, []);

	useEffect(() => {
		getPrivateServices();
		fetchGalleryImages();
		getAiTags();
	}, [getPrivateServices, fetchGalleryImages, getAiTags]);

	const refreshFunction = async (message?: string) => {
		await current_instructor.refetch();
		await getPrivateServices();
	};

	const handleDeactivate = async () => {
		try {
			await axios.put(`${constants.API_URL}instructor/${instructor_id}/deactivate`);
			redirect(listPath);
			notify('המומחה עודכן בהצלחה', { type: 'info' });
		} catch (error) {
			notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
		}
	};

	// GH-791 — "העבר לארכיון" (is_deleted). The expert leaves the admin lists; no restore from the admin.
	const handleArchive = async () => {
		setArchiving(true);
		try {
			const res = await axios.put(`${constants.API_URL}instructor/${instructor_id}/archive`);
			const future = Number(res?.data?.future_sessions || 0);
			notify('הועבר/ה לארכיון', { type: 'success' });
			if (future > 0) {
				notify(`שימו לב: ל${current_instructor?.record?.name || 'מומחה/ית'} יש ${future} פגישות עתידיות — הן לא בוטלו`, {
					type: 'warning',
					autoHideDuration: 12000,
				});
			}
			setArchiveOpen(false);
			redirect(listPath);
		} catch (error) {
			notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
		} finally {
			setArchiving(false);
		}
	};

	const handleDeleteImage = async (imageId: string) => {
		if (window.confirm('האם אתה בטוח שברצונך למחוק תמונה זו?')) {
			try {
				const token = localStorage.getItem('token');
				if (!token) {
					notify('No authentication token found', { type: 'error' });
					return;
				}

				await axios.post(
					`${constants.API_URL}instructor/delete_supplier_image`,
					{ image_id: imageId },
					{
						headers: {
							'Authorization': `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					}
				);

				notify('התמונה נמחקה בהצלחה', { type: 'success' });
				fetchGalleryImages(); // Refresh the gallery after deletion
			} catch (error) {
				console.error('Error deleting image:', error);
				notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
			}
		}
	};

	const handleConfirmStep = async (step: number) => {
		try {
			const token = localStorage.getItem('token');
			if (!token) {
				notify('No authentication token found', { type: 'error' });
				return;
			}

			switch (step) {
				case 1:
					await axios.post(`${constants.API_URL}instructor/join-instructor`, {
						id: instructor_id
					}, {
						headers: {
							'Authorization': `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					});

					break;
				case 3:
					await axios.post(`${constants.API_URL}instructor/join-instructor-step2`, {
						id: instructor_id
					}, {
						headers: {
							'Authorization': `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					});
					break;
					case 5:
					await axios.post(`${constants.API_URL}instructor/confirm-instructor-registration`, {
						id: instructor_id
					}, {
						headers: {
							'Authorization': `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					});
					break;
				default:
					break;
			}
			notify('אושר בהצלחה', { type: 'success' });
			await current_instructor.refetch();
		} catch (error) {
			notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
		}
	};

	const handleDenyStep = async (step: number) => {
		try {
			const token = localStorage.getItem('token');
			if (!token) {
				notify('No authentication token found', { type: 'error' });
				return;
			}

			switch (step) {
				case 1:
					await axios.post(`${constants.API_URL}instructor/deny-instructor-registration`, {
						id: instructor_id
					}, {
						headers: {
							'Authorization': `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					});

					break;
				case 3:
					await axios.post(`${constants.API_URL}instructor/deny-instructor-registration-step2`, {
						id: instructor_id
					}, {
						headers: {
							'Authorization': `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					});
					break;
					case 5:
					await axios.post(`${constants.API_URL}instructor/confirm-instructor-registration`, {
						id: instructor_id
					}, {
						headers: {
							'Authorization': `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					});
					break;
				default:
					break;
			}




			notify('נדחה בהצלחה', { type: 'success' });
			await current_instructor.refetch();
		} catch (error) {
			notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
		}
	};

	return (
		<Edit
			mutationMode="pessimistic"
			redirect={false}
			mutationOptions={{
				onSuccess: async () => {
					await refreshFunction();
					notify('המומחה עודכן בהצלחה', { type: 'info' });
				},
				onError: (e: any, _: any) => notify(e.response.data.message, { type: 'error' }),
			}}
			transform={(data: any) => {
				// Remove insuranceCertificate if it's an object (from ImageInput)
				// Keep it only if it's a string (existing value)
				if (data?.insuranceCertificate && typeof data.insuranceCertificate === 'object') {
					delete data.insuranceCertificate;
				}
				return data;
			}}
			title={
				current_instructor.record
					? `${current_instructor.record?.name || ''}\t\t|\t\t${current_instructor.record?._id || ''}`
					: '...'
			}
		>
			<TabbedForm
				toolbar={
					<Toolbar>
						<SaveButton />
						<InstructorFullExportButton />
					</Toolbar>
				}
			>
				<FormTab label={'info'}>
					<FunctionField
						render={(record: any) => (
							<Box
								sx={{
									mb: 3,
									p: 2,
									bgcolor: '#e3f2fd',
									borderRadius: 2,
									border: '1px solid #2196f3',
									maxWidth: '100%',
									minWidth: 0,
									overflow: 'hidden',
								}}
							>
								<Typography variant="h6" sx={{ mb: 1 }} dir="rtl">
									לינק לשלב 2
								</Typography>
								{record?.tempToken ? (
									<Typography
										variant="body1"
										dir="rtl"
										sx={{
											wordBreak: 'break-all',
											overflowWrap: 'anywhere',
											'& a': { color: '#1976d2', textDecoration: 'underline' },
										}}
									>
										<a
											href={`https://app.grouphug.online/register-expert-step2${record?.is_global ? '-en' : ''}/${record.tempToken}`}
											target="_blank"
											rel="noopener noreferrer"
										>
											https://app.grouphug.online/register-expert-step2{record?.is_global ? '-en' : ''}/{record.tempToken}
										</a>
									</Typography>
								) : (
									<Typography variant="body2" color="text.secondary" dir="rtl">
										אין token זמין
									</Typography>
								)}
							</Box>
						)}
					/>
					<FunctionField
						render={(record: any) =>
							record?.inStep === 1 && (
								<Box sx={{ mb: 3, p: 2, bgcolor: '#fff3cd', borderRadius: 2, border: '1px solid #ffc107' }}>
									<Grid container spacing={2} alignItems="center">
										<Grid item xs={12}>
											<Typography variant="h6" sx={{ mb: 2 }} dir="rtl">
												מילא טופס ראשון - דורש אישור
											</Typography>
										</Grid>
										<Grid item xs={12} sm={6}>
											<Button
												variant="contained"
												color="success"
												fullWidth
												onClick={()=>handleConfirmStep(1)}
												size="large"
											>
												אישור
											</Button>
										</Grid>
										<Grid item xs={12} sm={6}>
											<Button
												variant="contained"
												color="error"
												fullWidth
												onClick={()=>handleDenyStep(1)}
												size="large"
											>
												דחייה
											</Button>
										</Grid>
									</Grid>
								</Box>
							)
						}
					/>

                    <FunctionField
						render={(record: any) =>
							record?.inStep === 3 && (
								<Box sx={{ mb: 3, p: 2, bgcolor: '#fff3cd', borderRadius: 2, border: '1px solid #ffc107' }}>
									<Grid container spacing={2} alignItems="center">
										<Grid item xs={12}>
											<Typography variant="h6" sx={{ mb: 2 }} dir="rtl">
												מילא טופס שני - דורש אישור
											</Typography>
										</Grid>
										<Grid item xs={12} sm={6}>
											<Button
												variant="contained"
												color="success"
												fullWidth
												onClick={()=>handleConfirmStep(3)}
												size="large"
											>
												אישור
											</Button>
										</Grid>
										<Grid item xs={12} sm={6}>
											<Button
												variant="contained"
												color="error"
												fullWidth
												onClick={()=>handleDenyStep(3)}
												size="large"
											>
												דחייה
											</Button>
										</Grid>
									</Grid>
								</Box>
							)
						}
					/>

<FunctionField
						render={(record: any) =>
							record?.inStep === 5 && (
								<Box sx={{ mb: 3, p: 2, bgcolor: '#fff3cd', borderRadius: 2, border: '1px solid #ffc107' }}>
									<Grid container spacing={2} alignItems="center">
										<Grid item xs={12}>
											<Typography variant="h6" sx={{ mb: 2 }} dir="rtl">
											סיים הזנה באדמין - דורש אישור
											</Typography>
										</Grid>
										<Grid item xs={12} sm={6}>
											<Button
												variant="contained"
												color="success"
												fullWidth
												onClick={()=>handleConfirmStep(5)}
												size="large"
											>
												אישור
											</Button>
										</Grid>
										<Grid item xs={12} sm={6}>
											<Button
												variant="contained"
												color="error"
												fullWidth
												//onClick={()=>handleDenyStep(3)}
												size="large"
											>
												דחייה
											</Button>
										</Grid>
									</Grid>
								</Box>
							)
						}
					/>
					<Grid container spacing={2}>
						<Grid item sm={4}>
							<FunctionField
								render={(record: any) => (
									<Typography variant="subtitle1" textAlign={'right'}>
										ID: {record.id}
									</Typography>
								)}
							/>
						</Grid>

						<Box width={'100%'} />
						<Grid item xs={8}>
							<TextInput source="admin_info" fullWidth label="מידע למנהל" multiline rows={4} helperText="המידע נשאר רק באדמין" />
						</Grid>
						<Box width={'100%'} />
						<FormDataConsumer>
							{({ formData }) => (
								<>
									<Grid item sm={4}>
										<TextInput
											source="name"
											fullWidth
											label={formData?.is_global ? 'First Name' : 'שם'}
										/>
									</Grid>
									<Grid item sm={4}>
										<TextInput
											source="english_name"
											fullWidth
											label={formData?.is_global ? 'Last Name' : 'English name'}
										/>
									</Grid>
								</>
							)}
						</FormDataConsumer>
						<Grid item sm={4}>
							<SelectInput
								source="gender"
								fullWidth
								label="מגדר"
								choices={[
									{ id: 'male', name: 'זכר' },
									{ id: 'female', name: 'נקבה' },
									{ id: 'other', name: 'אחר' },
									{ id: 'Non-binary', name: 'Non-binary' },
									{ id: 'Prefer not to say', name: 'Prefer not to say' },
								]}
							/>
						</Grid>
						<Box width="100%" />
						<FormDataConsumer>
							{({ formData }) =>
								formData?.safe_space ? null : (
									<Grid item sm={4}>
										<ReferenceArrayInput reference="instructor-category/all" source="category" perPage={9999}>
											<SelectArrayInput
												fullWidth
												optionText={(record) =>
													`${record.__t === 'helping_hand' ? 'HH' : ''} ${record.hebrew_name}`
												}
												optionValue="id"
												style={{ marginTop: -1 }}
											/>
										</ReferenceArrayInput>
									</Grid>
								)
							}
						</FormDataConsumer>
						<Box width="100%" />
						<FormDataConsumer>
							{({ formData }) => (
								<Grid item sm={8} sx={{ display: formData?.is_global ? 'none' : undefined }}>
									<TextInput source="title" fullWidth />
								</Grid>
							)}
						</FormDataConsumer>
						<Grid item sm={8}>
							<TextInput source="english_title" fullWidth />
						</Grid>
						<Box width="100%" />
						<FormDataConsumer>
							{({ formData }) => (
								<Grid item sm={3} sx={{ display: formData?.is_global ? 'none' : undefined }}>
									<TextInput source="short_title.he" fullWidth inputProps={{}} />
								</Grid>
							)}
						</FormDataConsumer>
						<Grid item sm={3}>
							<TextInput source="short_title.en" fullWidth inputProps={{}} />
						</Grid>
						<Box width="100%" />
						<FormDataConsumer>
							{({ formData }) => (
								<Grid item sm={8} sx={{ display: formData?.is_global ? 'none' : undefined }}>
									<TextInput source="description_lng.he" multiline fullWidth label={'תיאור בעברית'} />
								</Grid>
							)}
						</FormDataConsumer>
						<Grid item sm={8}>
							<TextInput
								source="description_lng.en"
								multiline
								fullWidth
								label={'תיאור באנגלית'}
								dir="ltr"
							/>
						</Grid>
						<Box width="100%" />
						<FormDataConsumer>
							{({ formData }) => (
								<Grid item sm={3} sx={{ display: formData?.is_global ? 'none' : undefined }}>
									<TextInput source="location" multiline fullWidth label={'מיקום קליניקה'} dir="rtl" />
								</Grid>
							)}
						</FormDataConsumer>
						<FormDataConsumer>
							{({ formData }) => (
								<Grid item sm={3}>
									<TextInput
										source="languages"
										multiline
										fullWidth
										label={formData?.is_global ? 'Languages Spoken (comma separated)' : 'שפות (מופרד ב,)'}
										dir={formData?.is_global ? 'ltr' : 'rtl'}
										format={(value: string[]) => value?.join(',')}
										parse={(value: string) => value?.split(',')}
									/>
								</Grid>
							)}
						</FormDataConsumer>
						<FormDataConsumer>
							{({ formData }) =>
								formData?.is_global && (
									<>
										<Grid item sm={3}>
											<TextInput
												source="timezone"
												fullWidth
												label='Time Zone'
												dir='ltr'
											/>
										</Grid>
										<Grid item sm={3}>
											<TextInput
												source="based_location"
												fullWidth
												label='Address (City, Country)'
												dir='ltr'
											/>
										</Grid>
									</>
								)
							}
						</FormDataConsumer>
						<FormDataConsumer>
							{({ formData }) => (
								<Grid item sm={3}>
									{/* 15.9.2026: shown for global experts too — the server keeps it in sync with their services (online) */}
									<BooleanInput
										source="is_online"
										fullWidth
										label={'מקבל אונליין'}
										dir="ltr"
										defaultChecked
									/>
								</Grid>
							)}
						</FormDataConsumer>
						<FormDataConsumer>
							{({ formData }) => (
								<Grid item sm={3} sx={{ display: formData?.is_global ? 'none' : undefined }}>
									<BooleanInput source="is_psychologist" fullWidth label="פסיכולוג/ית" dir="ltr" />
								</Grid>
							)}
						</FormDataConsumer>
						<FormDataConsumer>
							{({ formData }) => (
								<Grid item sm={3} sx={{ display: formData?.is_global ? 'none' : undefined }}>
									<BooleanInput source="licensed_dealer" fullWidth label="עוסק מורשה" dir="ltr" />
								</Grid>
							)}
						</FormDataConsumer>
						<FormDataConsumer>
							{({ formData }) => (
								<Grid item sm={3} sx={{ display: formData?.is_global ? 'none' : undefined }}>
									<NumberInput
										source="safe_space_payment"
										fullWidth
										label="מחיר מסובסד למטפל (₪)"
										helperText="אם ריק — ברירת מחדל: 350 פסיכולוג / 250 עו״ס"
										min={0}
										dir="ltr"
									/>
								</Grid>
							)}
						</FormDataConsumer>

						<Box width="100%" />
						<Grid item sm={8}>
							<TextInput source="image" fullWidth />
							<ImageField source="image" />
						</Grid>
						<Grid item sm={8}>
						<FunctionField
							render={(record: any) => (
								<ImageInput source="image">
									<ImageField source={'src'} />
								</ImageInput>
							)}
						/>
					</Grid>

					<Box width="100%" />
					
					{/* Bank Details Section */}
					<FormDataConsumer>
						{({ formData }) => {
							const isGlobal = !!formData?.is_global;
							return (
								<>
									<Grid item xs={12}>
										<Typography variant="h6" sx={{ mt: 2, mb: 2 }} dir={isGlobal ? 'ltr' : 'rtl'}>
											{isGlobal ? 'Bank Details' : 'פרטי בנק'}
										</Typography>
									</Grid>
									<Grid item sm={4}>
										<TextInput
											source="bank_name"
											fullWidth
											label={isGlobal ? 'Bank Name' : 'שם הבנק'}
										/>
									</Grid>
									<Grid item sm={4}>
										<TextInput
											source="account_holder_name"
											fullWidth
											label={isGlobal ? 'Account Holder Name' : 'שם בעל חשבון'}
										/>
									</Grid>
									<Grid item sm={4}>
										<TextInput
											source="branch_number"
											fullWidth
											label={isGlobal ? 'Routing Number' : 'מספר סניף'}
										/>
									</Grid>
									<Grid item sm={4}>
										<TextInput
											source="account_number"
											fullWidth
											label={isGlobal ? 'Account Number' : 'מספר חשבון'}
										/>
									</Grid>
								</>
							);
						}}
					</FormDataConsumer>
					<Box width="100%" />
					
					{/* Certificate Section */}
					<FormDataConsumer>
						{({ formData }) => (
							<Grid
								item
								xs={12}
								sx={{ display: formData?.is_global && formData?.safe_space ? 'none' : undefined }}
							>
								<Typography variant="h6" sx={{ mt: 2, mb: 2 }} dir="rtl">
									תעודות
								</Typography>
							</Grid>
						)}
					</FormDataConsumer>
					<FormDataConsumer>
						{({ formData }) => (
							<Grid
								item
								xs={12}
								sx={{ display: formData?.is_global && formData?.safe_space ? 'none' : undefined }}
							>
						<Typography textAlign={'right'} variant="subtitle1" sx={{ mb: 1 }} dir="rtl">
							תעודת ביטוח
						</Typography>

						    <Button
                        variant='outlined'
                        onClick={() => {
                          const el = document.getElementById('insuranceCertificateInput') as HTMLInputElement | null;
                          el?.click();
                        }}
                      >
                        העלאת קובץ
                      </Button>
                      <input
                        id='insuranceCertificateInput'
                        type='file'
                        accept='image/*,application/pdf'
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = () => {
                            const result = reader.result as string;
                           // setValue('insuranceCertificate', result, { shouldDirty: true });
                            (async () => {
                              try {
                                await axios.post(`${constants.API_URL}/admin/expert-upload-insurance-certificate-admin`, {
                                  insuranceCertificate: result,
                                  instructor_id: instructor_id,
                                });
                                notify('התעודה הועלתה בהצלחה', { type: 'success' });
                                await current_instructor.refetch();
                              } catch (error: any) {
                                if (error instanceof AxiosError) {
                                  notify(
                                    getLocalizedErrorMessage(error, 'he') || 'שגיאה בהעלאת התעודה',
                                    { type: 'error' }
                                  );
                                } else {
                                  notify('שגיאה בהעלאת התעודה', { type: 'error' });
                                }
                              }
                            })();
                          };
                          reader.readAsDataURL(file);
                          e.currentTarget.value = '';
                        }}
                      />
						<FunctionField
							render={(record: any) => {
								const url = record?.insuranceCertificate as string | undefined;
								if (!url) {
									return null;
								}

								const isPdf = url.toLowerCase().endsWith('.pdf');
								if (isPdf) {
									return (
										<Typography variant="body2" dir="rtl" sx={{ fontWeight: 'bold', mb: 2 }}>
											<a href={url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 'bold' }}>
												קובץ תעודת ביטוח (PDF)
											</a>
										</Typography>
									);
								}

								return (
									<a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginBottom: 16 }}>
										<img
											src={url}
											alt={'insurance-certificate'}
											style={{ width: 300, maxWidth: '100%', height: 'auto', borderRadius: 8 }}
										/>
									</a>
								);
							}}
						/>
						{/* <ImageInput 
							source="insuranceCertificate" 
							label="העלאת תעודת ביטוח"
							accept="image/*,application/pdf"
							onChange={(event: any) => {
								const files = event?.target?.files || event?.rawFile || event;
								alert(files);
								if (!files) {
									return;
								}

								// If it's the ImageInput object structure, extract the rawFile
								const file = files instanceof File ? files : (event?.rawFile || files?.rawFile);
							
								if (!file) {
									return;
								}
								
								(async () => {
									try {
										const token = localStorage.getItem('token');
										if (!token) {
											notify('No authentication token found', { type: 'error' });
											return;
										}

										const formData = new FormData();
										formData.append('image', file instanceof File ? file : file.rawFile);
										formData.append('instructor_id', instructor_id.toString());
										formData.append('type', 'insurance_certificate');
									
										await axios.post(`${constants.API_URL}instructor/upload_image`, formData, {
											headers: {
												'Authorization': `Bearer ${token}`,
												'Accept': 'application/json'
											},
										});
										
										notify('תעודת ביטוח הועלתה בהצלחה', { type: 'success' });
										current_instructor.refetch();
									} catch (error) {
										console.error('Error uploading insurance certificate:', error);
										notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
									}
								})();
							}}
						>
							<ImageField source="src" />
						</ImageInput> */}
						<DateInput source="insuranceExpiryDate" label="תאריך תפוגת ביטוח" fullWidth sx={{ mt: 2 }} />
							</Grid>
						)}
					</FormDataConsumer>



					<FormDataConsumer>
						{({ formData }) => (
							<Grid
								item
								xs={12}
								sx={{ display: formData?.is_global && formData?.safe_space ? 'none' : undefined }}
							>
						<Typography textAlign={'right'} variant="subtitle1" sx={{ mt: 2, mb: 1 }} dir="rtl">
							תעודות מקצועיות
						</Typography>
						<FunctionField
							render={(record: any) => (
								<Grid container spacing={2}>
									{Array.isArray(record?.professionalCertificates) && record.professionalCertificates.length > 0 ? (
										record.professionalCertificates.map((url: string, index: number) => {
											const isPdf = url.toLowerCase().endsWith('.pdf');
											const fileName = url.split('/').pop() || `certificate-${index}`;
											
											return (
												<Grid item xs={6} sm={4} md={3} key={index}>
													{isPdf ? (
														<Box
															sx={{
																padding: 2,
																border: '1px solid #ccc',
																borderRadius: 8,
																backgroundColor: '#f5f5f5',
																display: 'flex',
																alignItems: 'center',
																justifyContent: 'center',
															}}
														>
															<a
																href={url}
																target="_blank"
																rel="noopener noreferrer"
																style={{ textDecoration: 'none', color: '#1976d2' }}
															>
																<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
																	{fileName}
																</Typography>
															</a>
														</Box>
													) : (
														<a href={url} onClick={(e) => openDocInNewTab(e, url)} target="_blank" rel="noopener noreferrer" style={{ cursor: 'pointer' }}>
															<img
																src={url}
																alt={`certificate-${index}`}
																style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 8 }}
															/>
														</a>
													)}
												</Grid>
											);
										})
									) : (
										<Typography variant="body2" color="text.secondary" dir="rtl">
											אין תעודות להצגה
										</Typography>
									)}
								</Grid>
							)}
						/>
						<Box sx={{ mt: 2, mb: 2 }}>
							<Button
								variant="outlined"
								onClick={() => {
									const el = document.getElementById('professionalCertificatesInput') as HTMLInputElement | null;
									el?.click();
								}}
								sx={{ mb: 2 }}
							>
								הוסף תעודות
							</Button>
							<input
								id="professionalCertificatesInput"
								type="file"
								accept="image/*,application/pdf"
								multiple
								style={{ display: 'none' }}
								onChange={(e) => {
									const files = Array.from(e.target.files || []);
									if (files.length > 0) {
										setProfessionalCertificateFiles((prev) => [...prev, ...files]);
									}
									e.currentTarget.value = '';
								}}
							/>
							{professionalCertificateFiles.length > 0 && (
								<Box sx={{ mt: 2 }}>
									<Grid container spacing={2}>
										{professionalCertificateFiles.map((file, index) => (
											<Grid item xs={6} sm={4} md={3} key={index}>
												<Box sx={{ position: 'relative' }}>
													{file.type.startsWith('image/') ? (
														<img
															src={URL.createObjectURL(file)}
															alt={`preview-${index}`}
															style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 8 }}
														/>
													) : (
														<Box
															sx={{
																width: '100%',
																padding: 2,
																border: '1px solid #ccc',
																borderRadius: 8,
																backgroundColor: '#f5f5f5',
																display: 'flex',
																alignItems: 'center',
																justifyContent: 'center',
															}}
														>
															<a
																href={URL.createObjectURL(file)}
																target="_blank"
																rel="noopener noreferrer"
																style={{ textDecoration: 'none', color: '#1976d2' }}
															>
																<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
																	{file.name}
																</Typography>
															</a>
														</Box>
													)}
													<Button
														variant="contained"
														color="error"
														size="small"
														onClick={() => {
															setProfessionalCertificateFiles((prev) => prev.filter((_, i) => i !== index));
														}}
														sx={{
															position: 'absolute',
															top: 8,
															right: 8,
															minWidth: 'auto',
															width: 32,
															height: 32,
															padding: 0,
														}}
													>
														<MdDelete />
													</Button>
												</Box>
											</Grid>
										))}
									</Grid>
									<Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
										<Button
											variant="contained"
											color="primary"
											disabled={isUploadingCertificates}
											onClick={async () => {
												if (professionalCertificateFiles.length === 0) {
													notify('אין קבצים להעלאה', { type: 'warning' });
													return;
												}

												setIsUploadingCertificates(true);
												try {
													// Convert all files to base64 array
													const base64Array = await Promise.all(
														professionalCertificateFiles.map((file) => {
															return new Promise<string>((resolve, reject) => {
																const reader = new FileReader();
																reader.onload = () => {
																	resolve(reader.result as string);
																};
																reader.onerror = reject;
																reader.readAsDataURL(file);
															});
														})
													);

													await axios.post(`${constants.API_URL}/admin/expert-upload-insurance-professionalCertificates-admin`, {
														professionalCertificates: base64Array,
														instructor_id: instructor_id,
													});

													notify('כל התעודות הועלו בהצלחה', { type: 'success' });
													setProfessionalCertificateFiles([]);
													await current_instructor.refetch();
												} catch (error: any) {
													if (error instanceof AxiosError) {
														notify(
															getLocalizedErrorMessage(error, 'he') || 'שגיאה בהעלאת התעודות',
															{ type: 'error' }
														);
													} else {
														notify('שגיאה בהעלאת התעודות', { type: 'error' });
													}
												} finally {
													setIsUploadingCertificates(false);
												}
											}}
										>
											שמור תעודות
										</Button>
										{isUploadingCertificates && (
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
												<CircularProgress size={20} />
												<Typography variant="body2" color="text.secondary">
													מעלה תעודות...
												</Typography>
											</Box>
										)}
									</Box>
								</Box>
							)}
						</Box>

<Grid item xs={12}>
						<Typography textAlign={'right'} variant="subtitle1" sx={{ mt: 2, mb: 1 }} dir="rtl">
						
						</Typography>
						<FunctionField
							render={(record: any) => {
								const url = record?.declarationDocument as string | undefined;
								if (!url) {
									return (
										<Typography variant="body2" color="text.secondary" dir="rtl">
										
										</Typography>
									);
								}

								const isPdf = url.toLowerCase().endsWith('.pdf');
								if (isPdf) {
									return (
										<Typography variant="body2" dir="rtl" sx={{ fontWeight: 'bold' }}>
											<a href={url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 'bold' }}>
												קובץ מסמך הצהרה (PDF)
											</a>
										</Typography>
									);
								}

								return (
									<a href={url} onClick={(e) => openDocInNewTab(e, url)} target="_blank" rel="noopener noreferrer" style={{ cursor: 'pointer' }}>
										<img
											src={url}
											alt={'declaration-document'}
											style={{ width: 300, maxWidth: '100%', height: 'auto', borderRadius: 8 }}
										/>
									</a>
								);
							}}
						/>
					</Grid>
					
					<Grid item xs={12}>
						<Typography textAlign={'right'} variant="subtitle1" sx={{ mt: 2, mb: 1 }} dir="rtl">
						
						</Typography>
						<FunctionField
							render={(record: any) => {
								const url = record?.supervisorDocument as string | undefined;
								if (!url) {
									return (
										<Typography variant="body2" color="text.secondary" dir="rtl">
										
										</Typography>
									);
								}

								const isPdf = url.toLowerCase().endsWith('.pdf');
								if (isPdf) {
									return (
										<Typography variant="body2" dir="rtl" sx={{ fontWeight: 'bold' }}>
											<a href={url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 'bold' }}>
												קובץ מסמך מפקח (PDF)
											</a>
										</Typography>
									);
								}

								return (
									<a href={url} onClick={(e) => openDocInNewTab(e, url)} target="_blank" rel="noopener noreferrer" style={{ cursor: 'pointer' }}>
										<img
											src={url}
											alt={'supervisor-document'}
											style={{ width: 300, maxWidth: '100%', height: 'auto', borderRadius: 8 }}
										/>
									</a>
								);
							}}
						/>
					</Grid>
					<FunctionField
						component="div"
						sx={{ display: 'contents' }}
						render={(record: any) => {
							const url = record?.w9_document as string | undefined;
							const path = url?.split('?')[0]?.toLowerCase() ?? '';
							if (!url || !path.endsWith('.pdf')) {
								return null;
							}
							return (
								<Grid item xs={12}>
									<Typography textAlign={'right'} variant="subtitle1" sx={{ mt: 2, mb: 1 }} dir="rtl">
										טופס W-9
									</Typography>
									<Typography variant="body2" dir="rtl" sx={{ fontWeight: 'bold' }}>
										<a href={url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 'bold' }}>
											קובץ W-9 (PDF)
										</a>
									</Typography>
								</Grid>
							);
						}}
					/>
							</Grid>
						)}
					</FormDataConsumer>
					<FunctionField
						component="div"
						sx={{ display: 'contents' }}
						render={(record: any) => {
							const licenses = Array.isArray(record?.licenses) ? record.licenses : [];
							if (licenses.length === 0) {
								return null;
							}
							const renderDocLink = (url: string, label: string) => {
								const lower = url.toLowerCase(); const noQuery = lower.split('?')[0]; const isPdf = lower.startsWith('data:application/pdf') || noQuery.endsWith('.pdf'); const isHeic = lower.startsWith('data:image/heic') || lower.startsWith('data:image/heif') || noQuery.endsWith('.heic') || noQuery.endsWith('.heif');
									// openDoc helper is hoisted to module scope (openDocInNewTab)
								if (isPdf || isHeic) {
									return (
										<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
											<a href={url} onClick={(e) => openDocInNewTab(e, url)} target="_blank" rel="noopener noreferrer" style={{ cursor: 'pointer' }}>
												{label} ({isPdf ? 'PDF' : 'HEIC'})
											</a>
										</Typography>
									);
								}
								return (
									<a href={url} onClick={(e) => openDocInNewTab(e, url)} target="_blank" rel="noopener noreferrer" style={{ cursor: 'pointer' }}>
										<img
											src={url}
											alt={label}
											style={{ width: 200, maxWidth: '100%', height: 'auto', borderRadius: 8 }}
										/>
									</a>
								);
							};
							return (
								<Grid item xs={12}>
									<Typography variant="h6" sx={{ mt: 3, mb: 2 }} dir="ltr">
										Professional Licensing Information
									</Typography>
									<Stack spacing={2}>
										{licenses.map((lic: any, idx: number) => (
											<Box
												key={idx}
												sx={{
													p: 2,
													border: '1px solid #ddd',
													borderRadius: 2,
													backgroundColor: '#fafafa',
												}}
												dir="ltr"
											>
												<Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
													License #{idx + 1}
												</Typography>
												<Grid container spacing={2}>
													<Grid item xs={12} sm={4}>
														<Typography variant="caption" color="text.secondary">
															License Type
														</Typography>
														<Typography variant="body2">{lic?.licenseType || '-'}</Typography>
													</Grid>
													<Grid item xs={12} sm={4}>
														<Typography variant="caption" color="text.secondary">
															State
														</Typography>
														<Typography variant="body2">{lic?.state || '-'}</Typography>
													</Grid>
													<Grid item xs={12} sm={4}>
														<Typography variant="caption" color="text.secondary">
															License Number
														</Typography>
														<Typography variant="body2">{lic?.licenseNumber || '-'}</Typography>
													</Grid>
													<Grid item xs={12} sm={4}>
														<Typography variant="caption" color="text.secondary">
															License Expiration
														</Typography>
														<Typography variant="body2">
															{lic?.licenseExpiration
																? moment(lic.licenseExpiration).format('YYYY-MM-DD')
																: '-'}
														</Typography>
													</Grid>
													<Grid item xs={12} sm={4}>
														<Typography variant="caption" color="text.secondary">
															NPI Number
														</Typography>
														<Typography variant="body2">{lic?.npiNumber || '-'}</Typography>
													</Grid>
													<Grid item xs={12} sm={4}>
														<Typography variant="caption" color="text.secondary">
															Insurance Expiration
														</Typography>
														<Typography variant="body2">
															{lic?.insuranceExpiration
																? moment(lic.insuranceExpiration).format('YYYY-MM-DD')
																: '-'}
														</Typography>
													</Grid>
													<Grid item xs={12}>
														<Typography variant="caption" color="text.secondary">
															Verification Link
														</Typography>
														<Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
															{lic?.verificationLink ? (
																<a
																	href={lic.verificationLink}
																	target="_blank"
																	rel="noopener noreferrer"
																>
																	{lic.verificationLink}
																</a>
															) : (
																'-'
															)}
														</Typography>
													</Grid>
													<Grid item xs={12} sm={6}>
														<Typography variant="caption" color="text.secondary">
															Insurance Certificate
														</Typography>
														{lic?.insuranceCertificate ? (
															renderDocLink(lic.insuranceCertificate, 'Insurance Certificate')
														) : (
															<Typography variant="body2">-</Typography>
														)}
													</Grid>
													<Grid item xs={12} sm={6}>
														<Typography variant="caption" color="text.secondary">
															Professional Certificates
														</Typography>
														{Array.isArray(lic?.professionalCertificates) &&
														lic.professionalCertificates.length > 0 ? (
															<Grid container spacing={1}>
																{lic.professionalCertificates.map(
																	(certUrl: string, cIdx: number) => (
																		<Grid item xs={6} key={cIdx}>
																			{renderDocLink(certUrl, `Certificate ${cIdx + 1}`)}
																		</Grid>
																	),
																)}
															</Grid>
														) : (
															<Typography variant="body2">-</Typography>
														)}
													</Grid>
												</Grid>
											</Box>
										))}
									</Stack>
								</Grid>
							);
						}}
					/>
					<Box width="100%" />


						<Box width={'100%'} />
						<Grid item sm={3}>
							<SelectInput
								fullWidth
								source="anchorImage"
								style={{ marginTop: 8 }}
								choices={[
									{ id: 'top', name: 'top' },
									{ id: 'center', name: 'center' },
									{ id: 'bottom', name: 'bottom' },
								]}
							/>
						</Grid>
						<Box width="100%" />
						<Grid item sm={3}>
							<TextInput source="email" fullWidth />
						</Grid>
						<Grid item sm={3}>
							<TextInput source="phone" fullWidth />
						</Grid>
						<Box width="100%" />
						<Grid item sm={3}>
							<TextInput source="button_text.he" label="טקסט כפתור (עב)" fullWidth />
						</Grid>
						<Grid item sm={3}>
							<TextInput source="button_text.en" label="טקסט כפתור (אנ)" fullWidth />
						</Grid>
						<Box width="100%" />
						<Grid item sm={6}>
							<TextInput source="zoom" fullWidth />
						</Grid>
						<Grid item sm={2}>
							<TextInput source="zoom_password" fullWidth />
						</Grid>
						<Grid item sm={3}>
							<NumberInput source="break_between_sessions" label="הפסקה בין פגישות (דקות)" fullWidth min={0} max={180} step={5} helperText="0 = ללא הפסקה נוספת" />
						</Grid>
						<Grid item sm={12}>
							{/* GH-761: read-only — the Google calendars the expert picked in the experts-app (names stored at save time) */}
							<FunctionField
								label="יומני Google שחוסמים זמינות"
								render={(record: any) => {
									const picked: { id: string; name: string }[] = Array.isArray(record?.busy_calendars) ? record.busy_calendars : [];
									return (
										<Box sx={{ fontSize: 13, color: '#555', mb: 2 }}>
											<Box component="span" sx={{ fontWeight: 600 }}>יומן ראשי</Box>
											{picked.length
												? picked.map((c) => (
														<Box component="span" key={c.id}>
															{' · '}
															{c.name}
														</Box>
													))
												: ' (ברירת מחדל — המומחה לא הוסיף יומנים; אפשר להוסיף בלשונית "סינכרון יומן" באפליקציה)'}
										</Box>
									);
								}}
							/>
							{/* GH-707: vacation ranges (saved immediately, not part of the form) */}
							<ExpertVacations instructorId={String(instructor_id)} />
						</Grid>
						<Grid item sm={6}>
							<TextInput source="url" fullWidth />
						</Grid>
						<Box width={'100%'} />

						<Grid item sm={6}>
							<TextInput source="helping_hand_url" fullWidth />
						</Grid>

						<Box width={'100%'} />
						<Grid item xs={2}>
							<BooleanInput source="safe_space" label="המרחב הבטוח" />
						</Grid>
						<FormDataConsumer>
							{({ formData }) =>
								formData.safe_space && (
									<>
									
									<Grid item xs={2}>
											<BooleanInput
												source="spectrom"
												label="ספקטרום"
											/>
										</Grid>
										<Grid item xs={2}>
											<BooleanInput
												source="safe_space_filter_trauma"
												label="טראומה ופוסט טראומה"
											/>
										</Grid>

										<Grid item xs={2}>
											<BooleanInput
												source="lost_and_grief"
												label="אובדן"
											/>
										</Grid>
										<Grid item xs={2}>
											<BooleanInput
												source="safe_space_filter_children_therapy_3_10"
												label="המרחב הבטוח - 3-10"
											/>
										</Grid>
										<Grid item xs={2}>
											<BooleanInput
												source="safe_space_filter_children_therapy_10_18"
												label="המרחב הבטוח - 10-18"
											/>
										</Grid>
										<Grid item xs={2}>
											<BooleanInput
												source="support_gays"
												label='התמחות בקהילת הלהט"ב'
											/>
										</Grid>
										<Grid item xs={2}>
											<BooleanInput
												source="depression_and_mood_state"
												label=" דיכאון וחרדה "
											/>
										</Grid>

										<Grid item xs={2}>
											<BooleanInput
												source="parenting_guidance"
												label="  הדרכת הורים "
											/>
										</Grid>

										
										<Grid item xs={2}>
											<BooleanInput
												source="personal_development"
												label="התפתחות אישית"
											/>
										</Grid>
										<Grid item xs={2}>
											<BooleanInput
												source="relationships_and_connections"
												label="מערכות יחסים"
											/>
										</Grid>
									
									
										
									</>
								)
							}
						</FormDataConsumer>
						<Grid item xs={2}>
							<BooleanInput source="showInSubscription" label="מופיע/ה במנוי" />
						</Grid>
						<Grid item xs={2}>
							<ReferenceArrayInput
								source="showForCompany"
								label="מופיע/ה לחברה"
								reference="company"
								perPage={100}
								sort={{ field: 'english_name', order: 'ASC' }}
							>
								<SelectArrayInput optionText="english_name" />
							</ReferenceArrayInput>
						</Grid>
						
						<Grid item xs={2}>
							<BooleanInput source="active" label="פעיל/ה" />
						</Grid>
						<Grid item xs={2}>
							<BooleanInput source="lnd" label="L&D" />
						</Grid>
						<Grid item xs={2}>
							<NumberInput source="priority" label="עדיפות" />
						</Grid>
						<Grid item xs={2}>
							<BooleanInput source="is_supplier" label="ספק" />
						</Grid>

						<Grid item xs={2}>
							<BooleanInput source="is_rehovot" label="רחובות? " />
						</Grid>

						<FormDataConsumer>
							{({ formData }) =>
								formData?.is_rehovot && (
									<Grid item xs={2}>
										<NumberInput
											source="is_rehovot_price"
											label="מחיר רחובות"
											fullWidth
										/>
									</Grid>
								)
							}
						</FormDataConsumer>
						
						<Grid item xs={2}>
							<NumberInput source="cancel_hours_before" label="מינ' שעות לפני ביטול פגישה" />
						</Grid>

						<Grid item xs={2}>
							<TextInput source="address" label="כתובת" fullWidth />
						</Grid>

						<Grid item xs={2}>
							<SelectArrayInput
								source="areas"
								label="אזורים"
								choices={[
									{ id: 'מרכז', name: 'מרכז' },
									{ id: 'צפון', name: 'צפון' },
									{ id: 'דרום', name: 'דרום' },
									{ id: 'שפלה', name: 'שפלה' },
									{ id: 'ירושלים והסביבה', name: 'ירושלים והסביבה' },
									{ id: 'שרון', name: 'שרון' },
								]}
								fullWidth
							/>
						</Grid>

						

						<Grid item xs={2}>
							<ImageInput 
								source="images" 
								label="גלרייה (נועד לספקים)"
								accept="image/*"
								multiple
								onChange={(event: any) => {
									// Handle both direct file array and event.target.files
									const files = event?.target?.files || event;
									
									if (!files || files.length === 0) {
										return;
									}
									
									// Convert FileList to Array if needed and ensure they are File objects
									const filesArray = Array.from(files).filter((file): file is File => file instanceof File);
									
									setSelectedFiles(filesArray);
								}}
							>
								<ImageField source="src" />
							</ImageInput>
							{selectedFiles.length > 0 && (
								<Button
									variant="contained"
									color="primary"
									onClick={async () => {
										try {
											const token = localStorage.getItem('token');
											if (!token) {
												notify('No authentication token found', { type: 'error' });
												return;
											}

											for (const file of selectedFiles) {
												const formData = new FormData();
												formData.append('image', file, file.name);
												formData.append('instructor_id', instructor_id.toString());
												formData.append('type', 'gallery');
												const response = await axios.post(
													`${constants.API_URL}instructor/upload_image`,
													formData,
													{
														headers: {
															'Authorization': `Bearer ${token}`,
															'Accept': 'application/json'
														}
													}
												);
											}
											
											notify('Images uploaded successfully', { type: 'success' });
											fetchGalleryImages(); // Refresh gallery after upload
											setSelectedFiles([]);
										} catch (error) {
											console.error('Error uploading images:', error);
											notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
										}
									}}
									style={{ marginTop: '10px' }}
								>
									העלה תמונות
								</Button>
							)}
							
						</Grid>
					</Grid>

					<Grid item xs={2}>
								<NumberInput source="coupons_price" label="הנחת קופון" />
							</Grid>

					<Grid item xs={2}>
							<SelectArrayInput
								source="ai_tags"
								label="תגיות AI"
								choices={aiTags.map((tag: any) => ({
									id: tag._id || tag.id,
									name: `${tag.name} (${tag.english_name})`,
								}))}
								fullWidth
							/>
						</Grid>
					<Box sx={{ mt: 5 }}>
								<Grid container spacing={2}>
									{galleryImages.map((image) => (
										<Grid item xs={6} sm={4} md={3} key={image.id}>
											<Box sx={{ position: 'relative' }}>
												<img 
													src={image.url} 
													alt="Gallery" 
													style={{ 
														width: '100%', 
														height: '200px',
														objectFit: 'cover',
														borderRadius: '8px'
													}}
												/>
												<Button
													variant="contained"
													color="error"
													size="small"
													onClick={() => handleDeleteImage(image._id)}
													sx={{
														position: 'absolute',
														top: 8,
														right: 8,
														minWidth: '32px',
														width: '32px',
														height: '32px',
														padding: 0
													}}
												>
													<MdDelete />
												</Button>
											</Box>
										</Grid>
									))}
								</Grid>
							</Box>

							<Typography variant="h6" sx={{ mt: 2, mb: 1 }} > תמונת רקע לספקים</Typography>
							<Grid item xs={2}>
								<ImageField source="background_url" label="תמונת רקע (נועד לספקים)" />
							</Grid>

							

				<Grid item xs={12}>
					<ImageInput 
						source="background_image" 
						label="העלאת תמונת רקע חדשה"
						accept="image/*"
						onChange={(event: any) => {
							console.log('onChange triggered');
							const files = event?.target?.files || event;
							console.log('Files:', files);
							
							if (!files) {
								console.log('No files selected');
								return;
							}

							(async () => {
								try {
									const token = localStorage.getItem('token');
									if (!token) {
										notify('No authentication token found', { type: 'error' });
										return;
									}

									const formData = new FormData();
									formData.append('image', files);
									formData.append('instructor_id', instructor_id.toString());
									formData.append('type', 'other');
								
									await axios.post(`${constants.API_URL}instructor/upload_image`, formData, {
										headers: {
											'Authorization': `Bearer ${token}`,
											'Accept': 'application/json'
										},
									});
									
									notify('תמונת רקע הועלתה בהצלחה', { type: 'success' });
									current_instructor.refetch();
								} catch (error) {
									console.error('Error uploading image:', error);
									notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
								}
							})();
						}}
					>
						<ImageField source="src" />
					</ImageInput>
				</Grid>
				</FormTab>

				
				<FormTab label="Recommendations">
					<Grid container>
						<Grid item sm={10}>
							<ArrayField source="recomendations">
								<Datagrid bulkActionButtons={false}>
									<TextField textAlign="right" source="name" />
									<TextField textAlign="right" source="text" />
									<TextField source="language" />
									<FunctionField
										render={(record: any) => (
											<Button
												onClick={async () => {
													await axios.delete(
														`${constants.API_URL}instructor/recommendation`,
														{
															data: {
																instructor_id: current_instructor?.record?.id,
																recommendation_id: record._id,
															},
														}
													);
													current_instructor.refetch();
												}}
											>
												<MdDelete />
											</Button>
										)}
									/>
								</Datagrid>
							</ArrayField>
						</Grid>
						<Box width="100%" mt={2} />
						<Grid item sm={2}>
							<Input
								placeholder="שם"
								value={newRecommendation.name}
								onChange={(e) => setNewRecommendation((prev) => ({ ...prev, name: e.target.value }))}
							/>
						</Grid>
						<Grid item sm={6}>
							<Input
								multiline
								placeholder="טקסט המלצה"
								fullWidth
								value={newRecommendation.text}
								onChange={(e) => setNewRecommendation((prev) => ({ ...prev, text: e.target.value }))}
							/>
						</Grid>
						<Grid item sm={2}>
							<Select
								multiline
								placeholder="שפה"
								fullWidth
								style={{ height: 30 }}
								value={newRecommendation.language}
								onChange={(e) =>
									setNewRecommendation((prev) => ({ ...prev, language: e.target.value }))
								}
							>
								<MenuItem value={'he'}>עברית</MenuItem>
								<MenuItem value={'en'}>אנגלית</MenuItem>
							</Select>
						</Grid>
						<Grid item sm={2}>
							<FunctionField
								render={(record: any) => (
									<Button
										onClick={async () => {
											await axios.post(`${constants.API_URL}instructor/recommendation`, {
												instructor_id: record.id,
												...newRecommendation,
											});

											setNewRecommendation({ name: '', text: '', language: 'he' });
											current_instructor.refetch();
										}}
									>
										{'הוספת המלצה'}
									</Button>
								)}
							/>
						</Grid>
					</Grid>
				</FormTab>
				<FormTab label="Private Services">
					<Stack spacing={2} width={'100%'}>
						<PrivateServicesTable
							privateServices={privateServices}
							refetch={refreshFunction}
							expert_id={instructor_id.toString()}
							safe_space={Boolean(current_instructor?.record?.safe_space)}
						/>
					</Stack>
				</FormTab>
				<FormTab label={<span className="text-red-600">DANGER</span>} color="red">
					<Stack direction="row" gap={2} flexWrap="wrap">
						<Button color="error" variant="contained" onClick={handleDeactivate}>
							<span>הגדר כ</span>
							<span className="font-bold ms-0">לא פעיל\ה</span>
						</Button>
						<Button color="error" variant="outlined" onClick={() => setArchiveOpen(true)}>
							העבר לארכיון
						</Button>
					</Stack>
					<Dialog open={archiveOpen} onClose={() => !archiving && setArchiveOpen(false)} maxWidth="sm" fullWidth>
						<DialogTitle>העברה לארכיון</DialogTitle>
						<DialogContent>
							<Typography sx={{ mb: 1.5 }}>
								{current_instructor?.record?.name || 'המומחה/ית'} ייעלם/תיעלם מרשימות המומחים והמטפלים באדמין. אין
								אפשרות להחזיר מהארכיון דרך האדמין.
							</Typography>
							{current_instructor?.record?.active !== false || current_instructor?.record?.showInSubscription ? (
								<>
									<Typography sx={{ fontWeight: 700, color: 'warning.dark' }}>שימו לב — הארכיון לבד לא מסתיר מהעמיתים:</Typography>
									{current_instructor?.record?.active !== false ? (
										<Typography>
											• עדיין מסומן/ת &quot;פעיל/ה&quot; — יכול/ה להיכנס לדשבורד ולהופיע לעמיתים ברשימות.
										</Typography>
									) : null}
									{current_instructor?.record?.showInSubscription ? (
										<Typography>
											• עדיין &quot;מופיע/ה במנוי&quot; — עשוי/ה להופיע לעמיתים בהמלצות ובהצעות.
										</Typography>
									) : null}
									<Typography sx={{ mt: 1 }}>מומלץ לכבות את שניהם ולשמור לפני ההעברה לארכיון.</Typography>
								</>
							) : (
								<Typography>כבר לא פעיל/ה ולא מופיע/ה במנוי — לא יופיע/תופיע לעמיתים.</Typography>
							)}
						</DialogContent>
						<DialogActions>
							<Button onClick={() => setArchiveOpen(false)} disabled={archiving}>
								ביטול
							</Button>
							<Button color="error" variant="contained" onClick={handleArchive} disabled={archiving}>
								{archiving ? 'מעביר…' : 'העבר לארכיון'}
							</Button>
						</DialogActions>
					</Dialog>
				</FormTab>
			</TabbedForm>
		</Edit>
	);
};

export default ExpertEdit;
